import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { Topic } from "@/mocks/topics";
import { categories } from "@/mocks/categories";
import TopicCard from "@/components/feature/TopicCard";

interface CategoryFilterSectionProps {
  topics: Topic[];
}

export default function CategoryFilterSection({ topics }: CategoryFilterSectionProps) {
  const [active, setActive] = useState("all");

  const tabs = useMemo(
    () => [{ id: "all", name: "全部档案", icon: "ri-apps-2-line" }, ...categories],
    []
  );

  const filtered = useMemo(() => {
    if (active === "all") return topics;
    return topics.filter((topic) => topic.categoryId === active);
  }, [active, topics]);

  const activeCategory = categories.find((c) => c.id === active);

  return (
    <section id="categories" className="scroll-mt-24 bg-background-100 py-16 md:py-20">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <span className="text-xs font-medium tracking-[0.3em] text-primary-600">
              DEEP DIVE
            </span>
            <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              按栏目浏览深度档案
            </h2>
            <p className="mt-4 text-sm leading-7 text-foreground-600">
              三个栏目对应三种追问方式：商业起底关注「钱怎么流动」，行业内幕关注「规则怎么运作」，舆情深挖关注「信息怎么扩散」。
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 rounded-full border border-background-300 bg-background-50 px-1 py-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActive(tab.id)}
                className={`flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  active === tab.id
                    ? "bg-foreground-950 text-background-50"
                    : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                <i className={tab.icon} />
                {tab.name}
              </button>
            ))}
          </div>
        </div>

        {activeCategory && (
          <p className="mt-6 rounded-lg border border-secondary-200 bg-secondary-50 px-4 py-3 text-sm text-secondary-900">
            {activeCategory.name}：{activeCategory.desc}
          </p>
        )}

        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((topic) => (
            <TopicCard key={topic.id} topic={topic} />
          ))}
        </div>

        <div className="mt-10 flex justify-center">
          <Link
            to={active === "all" ? "/topics" : `/topics?category=${active}`}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-foreground-950 px-6 py-3 text-sm font-medium text-foreground-950 transition-colors hover:bg-foreground-950 hover:text-background-50"
          >
            浏览该栏目全部档案
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}